/**
 * On every Supabase auth change, if the active session was established via
 * Google OAuth, persist the Google linkage onto the user's `profiles` row
 * (keyed by device_id `auth_${userId}`) so the link survives across devices.
 *
 * Idempotent — only writes when the stored values differ from the session.
 */
import { supabase } from "@/integrations/supabase/client";

export async function syncGoogleLinkage(userId: string): Promise<void> {
  try {
    const { data: u } = await supabase.auth.getUser();
    const user = u?.user;
    if (!user || user.id !== userId) return;

    const identities = (user.identities || []) as Array<{
      provider: string;
      identity_data?: Record<string, any>;
      id?: string;
    }>;
    const providers = Array.from(new Set(identities.map((i) => i.provider).filter(Boolean)));
    const google = identities.find((i) => i.provider === "google");

    const deviceId = `auth_${userId}`;
    try { localStorage.setItem("rufayq_device_id", deviceId); } catch { /* ignore */ }

    const { data: existing } = await supabase
      .from("profiles")
      .select("device_id, google_sub, google_email, auth_providers, email")
      .eq("device_id", deviceId)
      .maybeSingle();

    const googleSub = google?.identity_data?.sub || google?.id || null;
    const googleEmail = google?.identity_data?.email || user.email || null;

    const next: Record<string, any> = {
      device_id: deviceId,
      auth_providers: providers,
    };
    if (google) {
      next.google_sub = googleSub;
      next.google_email = googleEmail;
      if (!existing?.google_sub) next.google_linked_at = new Date().toISOString();
    }
    // Backfill the canonical email used by Chat Discovery if the profile has none.
    const backfillEmail = !existing?.email && (googleEmail || user.email) ? (googleEmail || user.email) : null;
    if (backfillEmail) next.email = backfillEmail;

    // Skip the write if nothing meaningful changed.
    if (
      existing &&
      existing.google_sub === googleSub &&
      existing.google_email === googleEmail &&
      JSON.stringify(existing.auth_providers || []) === JSON.stringify(providers) &&
      !backfillEmail
    ) {
      return;
    }

    const { error } = await supabase.from("profiles").upsert(next as any, { onConflict: "device_id" });
    if (error) console.warn("[google-link] upsert failed", error.message);
  } catch (e) {
    console.warn("[google-link] sync error", e);
  }
}

/**
 * Clear cached Google linkage from the profile row after the user unlinks the
 * Google identity. Best-effort; safe to call when no row exists.
 */
export async function clearGoogleLinkage(userId: string): Promise<void> {
  try {
    const deviceId = `auth_${userId}`;
    const { data: existing } = await supabase
      .from("profiles")
      .select("device_id, auth_providers")
      .eq("device_id", deviceId)
      .maybeSingle();
    if (!existing) return;
    const providers = (existing.auth_providers || []).filter((p: string) => p !== "google");
    const { error } = await supabase
      .from("profiles")
      .update({
        google_email: null,
        google_sub: null,
        google_linked_at: null,
        auth_providers: providers,
      } as any)
      .eq("device_id", deviceId);
    if (error) console.warn("[google-link] clear failed", error.message);
  } catch (e) {
    console.warn("[google-link] clear error", e);
  }
}

/**
 * Generic provider clear — keeps the auth_providers cache in sync after the user
 * unlinks any non-Google identity (e.g. Apple). Google has its own dedicated
 * clearer above because we also persist `google_email` / `google_sub` columns.
 */
export async function clearProviderLinkage(
  userId: string,
  provider: string
): Promise<void> {
  try {
    const deviceId = `auth_${userId}`;
    const { data: existing } = await supabase
      .from("profiles")
      .select("device_id, auth_providers")
      .eq("device_id", deviceId)
      .maybeSingle();
    if (!existing) return;
    const providers = (existing.auth_providers || []).filter(
      (p: string) => p !== provider
    );
    const { error } = await supabase
      .from("profiles")
      .update({ auth_providers: providers } as any)
      .eq("device_id", deviceId);
    if (error) console.warn("[provider-link] clear failed", error.message);
  } catch (e) {
    console.warn("[provider-link] clear error", e);
  }
}
